import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CalendarDays, Sparkles, Clock, Award, Music2, Mic2, Drama } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import { useChantiers } from "../lib/useChantiers";
import { useReglages } from "../lib/useReglages";
import { duree as dureeTexte, franc, prixDe, tarifsGroupes } from "../lib/reglesPrives";
import ReservationCoursPrive from "./ReservationCoursPrive";

/**
 * Catalogue des créneaux Calendly par discipline.
 * Chaque option = 1 durée = 1 URL Calendly dédiée. Le prix affiché est celui des Réglages ›
 * Cours privés (02.11.2026, `GET /api/reglages`) ; celui écrit ici n'est que le repli.
 */
const DISCIPLINES = [
  {
    id: "danse",
    label: "Danse",
    icon: Music2,
    tagline: "Hip-hop, classique, contemporain, heels…",
    options: [
      { id: "danse-60", label: "60 min", price: "CHF 120.–", url: "https://calendly.com/noemie-vaney/cours-de-danse-prive-60-min-def" },
      { id: "danse-90", label: "90 min", price: "CHF 150.–", url: "https://calendly.com/noemie-vaney/cours-prive-de-danse-90-mim-def" },
    ],
  },
  {
    id: "chant",
    label: "Chant",
    icon: Mic2,
    tagline: "Technique vocale, interprétation, respiration.",
    options: [
      { id: "chant-30", label: "30 min", price: "CHF 60.–", url: "https://calendly.com/noemie-vaney/cours-de-chant-30-minutes" },
      { id: "chant-45", label: "45 min", price: "CHF 75.–", url: "https://calendly.com/noemie-vaney/cours-de-chant-45-minutes" },
      { id: "chant-60", label: "60 min", price: "CHF 90.–", url: "https://calendly.com/noemie-vaney/cours-de-chant-60-minutes" },
    ],
  },
  {
    id: "theatre",
    label: "Théâtre",
    icon: Drama,
    tagline: "Jeu, présence scénique, texte, improvisation.",
    options: [
      { id: "theatre-30", label: "30 min", price: "CHF 60.–", url: "https://calendly.com/noemie-vaney/15min" },
      { id: "theatre-45", label: "45 min", price: "CHF 75.–", url: "https://calendly.com/noemie-vaney/30min" },
      { id: "theatre-60", label: "60 min", price: "CHF 90.–", url: "https://calendly.com/noemie-vaney/60min" },
    ],
  },
];

/**
 * Charge le script Calendly (idempotent) et (ré)initialise EXPLICITEMENT le widget
 * dans le container fourni via `window.Calendly.initInlineWidget`.
 * Se déclenche à chaque changement d'URL (donc à chaque nouvelle option choisie).
 */
function useCalendlyInline(containerRef, url) {
  useEffect(() => {
    if (!url) return;
    let cancelled = false;

    const initWidget = () => {
      if (cancelled) return;
      if (window.Calendly && containerRef.current) {
        containerRef.current.innerHTML = "";
        window.Calendly.initInlineWidget({
          url,
          parentElement: containerRef.current,
        });
      }
    };

    if (window.Calendly) {
      setTimeout(initWidget, 50);
      return () => { cancelled = true; };
    }

    const existing = document.querySelector('script[data-calendly="1"]');
    if (existing) {
      existing.addEventListener("load", initWidget, { once: true });
      return () => {
        cancelled = true;
        existing.removeEventListener("load", initWidget);
      };
    }

    const s = document.createElement("script");
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    s.setAttribute("data-calendly", "1");
    s.onload = initWidget;
    document.body.appendChild(s);

    return () => { cancelled = true; };
  }, [containerRef, url]);
}

/* Cours privés : la réservation de la console (version de test d'abord, école 30.10.2026)
   remplace Calendly là où le chantier est ouvert ; ailleurs, Calendly reste en place. */
export default function CoursPrivesForm() {
  const chantiers = useChantiers();
  return chantiers.cours_prives ? <ReservationCoursPrive /> : <CoursPrivesCalendly />;
}

function CoursPrivesCalendly() {
  useSEO({
    title: "Réserver un cours privé — 1000 Arts Cugy VD",
    description: "Danse, chant ou théâtre — un accompagnement 100 % personnalisé avec le prof de ton choix. Choisis ta discipline, ta durée et réserve directement un créneau.",
    canonical: "/inscription/cours-prive",
  });

  const [disciplineId, setDisciplineId] = useState(null);
  const [optionId, setOptionId] = useState(null);
  const widgetRef = useRef(null);

  // Les prix des Réglages › Cours privés (02.11.2026), appliqués sur place à leur arrivée.
  const reglages = useReglages();
  const prixOption = (o) => {
    const [disc, min] = o.id.split("-");
    const p = prixDe(disc, Number(min), null);
    return p === null ? o.price : `CHF ${franc(p)}`;
  };
  // Le bandeau des tarifs : la danse d'abord, puis le chant et le théâtre (qui partagent leurs
  // lignes quand leurs prix sont les mêmes), chaque durée de la plus courte à la plus longue.
  const tarifs = useMemo(() => [...tarifsGroupes()].sort((a, b) => Number(b.labels.includes("Danse")) - Number(a.labels.includes("Danse")))
    .flatMap((g, i) => g.durees.map((x, k) => ({ cle: `${g.cle}-${x.minutes}`, texte: `${g.labels.join(" / ")} · ${dureeTexte(x.minutes)}`,
      prix: `CHF ${franc(x.prix)}`, separe: i > 0 && k === 0 }))), [reglages]); // eslint-disable-line react-hooks/exhaustive-deps
  const discipline = DISCIPLINES.find((d) => d.id === disciplineId) || null;
  const option = discipline?.options.find((o) => o.id === optionId) || null;

  useCalendlyInline(widgetRef, option?.url);

  // Quand on change de discipline, on réinitialise l'option.
  useEffect(() => {
    setOptionId(null);
  }, [disciplineId]);

  return (
    <div data-testid="page-cours-prives" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 relative z-10">
        <Link to="/inscription" className="btn-ghost mb-6 inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Retour aux inscriptions
        </Link>

        <div className="eyebrow mb-3">Cours privés · Réservation en ligne</div>
        <h1 className="font-display text-4xl md:text-6xl tracking-tight leading-tight">
          Réserve ton créneau<br /><span style={{ color: "var(--primary)" }}>de cours privé</span>
        </h1>
        <p className="mt-6 text-lg text-[var(--muted)] max-w-2xl">
          Choisis ta discipline, sélectionne la durée qui te correspond, puis pique directement un créneau disponible dans le calendrier.
        </p>

        {/* Bandeau info tarifs & format */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <div className="card-flat" data-testid="cp-info-disciplines">
            <div className="flex items-center gap-2 mb-3" style={{ color: "var(--primary)" }}>
              <Sparkles size={16} /> <span className="eyebrow text-[10px]">Disciplines</span>
            </div>
            <p className="text-base font-medium">Danse · Chant · Théâtre.</p>
          </div>
          <div className="card-flat" data-testid="cp-info-tarifs">
            <div className="flex items-center gap-2 mb-3" style={{ color: "var(--primary)" }}>
              <Award size={16} /> <span className="eyebrow text-[10px]">Tarifs</span>
            </div>
            <ul className="text-sm space-y-1.5">
              {tarifs.map((t) => (
                <li key={t.cle} className={`flex items-baseline justify-between gap-3${t.separe ? " pt-1.5" : ""}`}
                  style={t.separe ? { borderTop: "1px solid var(--border-soft)" } : undefined}>
                  <span className="text-[var(--text)]">{t.texte}</span>
                  <strong style={{ color: "var(--primary)" }}>{t.prix}</strong>
                </li>
              ))}
            </ul>
          </div>
          <div className="card-flat" data-testid="cp-info-format">
            <div className="flex items-center gap-2 mb-3" style={{ color: "var(--primary)" }}>
              <Clock size={16} /> <span className="eyebrow text-[10px]">Format</span>
            </div>
            <p className="text-base font-medium">Cours individuel, dans nos salles à Cugy VD.</p>
          </div>
        </div>

        {/* Étape 1 — choix de la discipline */}
        <section className="mt-12" data-testid="cp-step-discipline">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: "var(--primary)" }}>1</div>
            <h2 className="font-display text-2xl md:text-3xl">Quelle discipline&nbsp;?</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {DISCIPLINES.map((d) => {
              const Icon = d.icon;
              const active = disciplineId === d.id;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setDisciplineId(d.id)}
                  data-testid={`cp-discipline-${d.id}`}
                  className="text-left rounded-3xl p-6 transition-all"
                  style={{
                    background: active ? "var(--primary)" : "#fff",
                    color: active ? "#fff" : "var(--text)",
                    border: active ? "2px solid var(--primary)" : "2px solid var(--border-soft)",
                    boxShadow: active ? "0 14px 40px rgba(20,184,196,0.25)" : "0 4px 14px rgba(15,27,45,0.05)",
                    transform: active ? "translateY(-2px)" : "none",
                  }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: active ? "rgba(255,255,255,0.2)" : "var(--primary-tint)", color: active ? "#fff" : "var(--primary)" }}>
                      <Icon size={22} />
                    </div>
                    <div className="font-display text-xl">{d.label}</div>
                  </div>
                  <p className="text-sm" style={{ color: active ? "rgba(255,255,255,0.85)" : "var(--muted)" }}>{d.tagline}</p>
                </button>
              );
            })}
          </div>
        </section>

        {/* Étape 2 — choix de la durée */}
        {discipline && (
          <section className="mt-12" data-testid="cp-step-duree">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: "var(--primary)" }}>2</div>
              <h2 className="font-display text-2xl md:text-3xl">Quelle durée&nbsp;?</h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {discipline.options.map((o) => {
                const active = optionId === o.id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setOptionId(o.id)}
                    data-testid={`cp-option-${o.id}`}
                    className="rounded-3xl p-5 transition-all text-left"
                    style={{
                      background: active ? "var(--primary-tint)" : "#fff",
                      border: active ? "2px solid var(--primary)" : "2px solid var(--border-soft)",
                      boxShadow: active ? "0 10px 30px rgba(20,184,196,0.15)" : "0 4px 14px rgba(15,27,45,0.04)",
                    }}
                  >
                    <div className="flex items-center gap-2 mb-2" style={{ color: "var(--primary)" }}>
                      <Clock size={16} />
                      <span className="eyebrow text-[10px]">Durée</span>
                    </div>
                    <div className="font-display text-xl mb-1">{o.label}</div>
                    <div className="text-sm font-semibold" style={{ color: "var(--primary)" }}>{prixOption(o)}</div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Étape 3 — widget Calendly */}
        {option && (
          <section className="mt-12" data-testid="cp-step-calendly">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: "var(--primary)" }}>3</div>
              <h2 className="font-display text-2xl md:text-3xl">Choisis ton créneau</h2>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-lg" data-testid="cp-calendly-wrap">
              <div className="flex items-center gap-2 px-6 py-3 text-sm font-medium" style={{ background: "var(--primary)", color: "#fff" }}>
                <CalendarDays size={16} /> {discipline.label} · {option.label} · {prixOption(option)}
              </div>
              <div
                key={option.id}
                ref={widgetRef}
                data-testid="cp-calendly-widget"
                style={{ minWidth: 320, height: 720, background: "#fff" }}
              />
            </div>
          </section>
        )}

        <p className="mt-8 text-xs text-[var(--muted)] text-center">
          Alternative : envoie-nous directement un mail à{" "}
          <a href="mailto:info1000arts@gmail.com" className="underline" style={{ color: "var(--primary)" }}>info1000arts@gmail.com</a>{" "}
          si tu ne trouves pas de créneau qui te convient.
        </p>
      </div>
    </div>
  );
}
